<template>
  <div style="height: 90vh;">
    <Alert class="bottom-alert" />
    <PeopleFilters
      v-if="showFilters"
      :filters.sync="filters"
      :search.sync="search"
      :status-options="availableStatuses"
      :tag-options="tagOptions"
      :group-options="groupOptions"
      :location-options="locationOptions"
      class="mb-3"
    />
    <div
      v-if="totalItems > 0 || hasActiveFilters"
      style="height: calc(90vh - 120px); overflow-y: auto;"
    >
      <v-col class="mt-0 pt-0">
        <PeopleTable
          :headers="headers"
          :items="normalizedOperators"
          :page.sync="page"
          :page-count="pageCount"
          :total="totalItems"
          :items-per-page.sync="itemsPerPage"
          :items-per-page-options="itemsPerPageOptions"
          :is-dark="$vuetify.theme.dark"
          @row-click="goToOperator"
        />
      </v-col>
    </div>
    <v-row
      v-else-if="showEmptyState"
      class="fill-height"
      align="center"
      justify="center"
      style="min-height: 70vh;"
    >
      <v-col cols="12" md="6" lg="4">
        <EmptyStateButton
          :items="getOperators"
          title="Operator"
          message="List of all operator will be shown here."
          button-text="Create operator"
          @create="createOperator"
        />
      </v-col>
    </v-row>

    <FlyoutMenu/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Alert from '../components/Alert'
import EmptyStateButton from '../components/Accounts/EmptyStateButton.vue'
import PeopleFilters from '../components/Accounts/PeopleFilters.vue'
import PeopleTable from '../components/Accounts/PeopleTable.vue'
import FlyoutMenu from '../components/FlyoutMenu.vue'
import Tags from '@/services/api/Tags'

export default {
  name: 'Operators',
  components: { Alert, PeopleTable, PeopleFilters, EmptyStateButton, FlyoutMenu },

  data() {
    return {
      search: '',
      filters: { status: [], tags: [], groups: [], locations: [] },
      operatorTags: [],
      hadOperators: false,

      page: 1,
      itemsPerPage: 25,
      itemsPerPageOptions: [10, 25, 50, 100],
      debounceTimer: null,

      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Username', value: 'username' },
        { text: 'Name', value: 'name' },
        { text: 'Gender', value: 'gender' },
        { text: 'E-mail', value: 'email' },
        { text: 'Phone', value: 'phone' },
        { text: 'Status', value: 'status' },
        { text: 'Tag', value: 'tag' },
        { text: 'Group', value: 'group' },
        { text: 'Location', value: 'location' },
        { text: 'Last seen', value: 'lastSeen' },
      ],
    }
  },

  computed: {
    ...mapGetters({
      getOperators:   'operators/getOperators',
      operatorsMeta:  'operators/getOperatorsMeta',
      allGroups:      'operators/getGroups',
      allLocations:   'locations/getLocations',
      operatorsLoading: 'operators/isLoading',
    }),

    // Hidden only for a space with no operators at all, so the create-operator
    // empty state stays alone as before.
    showFilters() {
      return this.totalItems > 0 || this.hasActiveFilters || this.hadOperators
    },
    // Never flash the create-operator state while the first fetch is in flight.
    showEmptyState() {
      return !this.operatorsLoading.operators
    },

    availableStatuses: () => ['Enabled', 'Disabled'],
    tagOptions() {
      return (this.operatorTags || [])
        .filter(t => t.uuid && t.name)
        .map(t => ({ text: t.name, value: t.uuid }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    groupOptions() {
      return (this.allGroups || [])
        .filter(g => g.uuid && g.name)
        .map(g => ({ text: g.name, value: g.uuid }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    locationOptions() {
      return (this.allLocations || [])
        .filter(l => l.uuid && l.name)
        .map(l => ({ text: l.name, value: l.uuid }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    hasActiveFilters() {
      return this.search.trim() !== '' ||
        Object.values(this.filters).some(arr => arr.length > 0)
    },

    totalItems() {
      return this.operatorsMeta?.count || 0
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage))
    },

    // API query params built from current filter + pagination state
    queryParams() {
      const params = {
        page:      this.page,
        page_size: this.itemsPerPage,
      }

      // status: send only when exactly one option is selected
      if (this.filters.status.length === 1) {
        params.enabled = this.filters.status[0] === 'Enabled'
      }
      if (this.filters.tags.length)      params.tag_uuid      = this.filters.tags
      if (this.filters.groups.length)    params.group_uuid    = this.filters.groups
      if (this.filters.locations.length) params.location_uuid = this.filters.locations
      if (this.search.trim())            params.search        = [this.search.trim()]

      return params
    },

    // Normalise raw API objects for display in PeopleTable
    normalizedOperators() {
      return (this.getOperators || []).map(i => ({
        uuid:     i.uuid,
        id:       i.id,
        username: i.username || '',
        name:     `${i.first_name || ''} ${i.last_name || ''}`.trim(),
        online:   i.online || false,
        phone:    i.mobile_phone || '',
        status:   i.enabled ? 'Enabled' : 'Disabled',
        lastSeen: i.last_seen_at || '',
        email:    i.email || '',
        gender:   i.gender || 'N/A',
        tag:      Array.isArray(i.tags)
                    ? i.tags.map(t => t.name || t).join(', ')
                    : (i.tag || 'N/A'),
        group:    Array.isArray(i.operator_groups)
                    ? i.operator_groups.map(g => g.name || g).join(', ')
                    : (i.space_name || 'N/A'),
        location: Array.isArray(i.locations)
                    ? i.locations.map(l => l.name || l).join(', ')
                    : (i.location_name || 'N/A'),
      }))
    },
  },

  watch: {
    // once a space is known to have operators, keep the filter bar visible
    totalItems(val) {
      if (val > 0) this.hadOperators = true
    },

    // reset to page 1 whenever filters or search change
    filters:      { deep: true, handler() { this.page = 1 } },
    search()      { this.page = 1 },
    itemsPerPage(){ this.page = 1 },

    // debounce all API calls through a single queryParams watcher
    queryParams: {
      deep: true,
      handler() {
        clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(() => this.fetchOperators(this.queryParams), 700)
      },
    },
  },

  methods: {
    ...mapActions({
      fetchOperators:      'operators/fetchOperators',
      fetchOperatorGroups: 'operators/fetchOperatorGroups',
      getLocations:        'locations/getLocations',
    }),
    fetchTags() {
      return Tags.get('operator').then(response => {
        this.operatorTags = response.data.tags
      })
    },
    createOperator() {
      this.$router.push({ name: 'addOperator', params: { action: 'addOperator', progress: 1 } })
    },
    goToOperator(row) {
      if (!row?.uuid) return
      this.$router.push({ name: 'viewOperator', params: { uuid: row.uuid, action: 'view' } })
    },
  },

  mounted() {
    this.fetchOperators(this.queryParams)
    // pre-fetch options for filter dropdowns
    this.fetchOperatorGroups('page_size=200')
    this.fetchTags()
    this.getLocations()
  },
}
</script>

<style scoped>
.bottom-alert {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
}
</style>
